// ShareBite typed client API layer (Task 13 / 14.1).
//
// The single boundary between the React UI and the Firebase backend. Every
// privileged operation goes through an authenticated Firebase callable
// function; list/read operations use guarded Firestore reads. The UI is
// untrusted — this module never enforces a rule itself; it only invokes the
// server-authoritative callables and surfaces typed results.
//
// Security note: authentication is Google-only (Firebase Auth) and all
// mutations flow through `httpsCallable`. No business decision is made here.
//
// In tests this module is mocked (vi.mock) so components can be exercised
// without a live Firebase project.

import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type User as FirebaseUser,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";
import type {
  Achievement,
  Donation,
  FoodCategory,
  NGOProfile,
  Notification,
  Rating,
  Report,
  ReportReason,
  RestaurantProfile,
  Role,
  VerificationStatus,
} from "@sharebite/domain";
import { auth, db, functions, googleProvider, storage } from "./firebase";

// --- Session / routing ------------------------------------------------------

export interface Session {
  uid: string;
  role: Role | null;
  verificationStatus: VerificationStatus;
}

export type SessionRoute =
  | { destination: "role-selection" }
  | { destination: "awaiting-verification" }
  | { destination: "dashboard"; role: Role };

export interface ResolvedSession {
  session: Session;
  route: SessionRoute;
}

// --- Registration payloads --------------------------------------------------

export interface DocumentMeta {
  format: string;
  sizeBytes: number;
}

export interface RegisterRestaurantInput {
  restaurantName: string;
  ownerName: string;
  phoneNumber: string;
  address: string;
  foodCategoriesServed: FoodCategory[];
  document: DocumentMeta;
  verificationDocRef: string;
}

export interface RegisterNGOInput {
  ngoName: string;
  representativeName: string;
  phoneNumber: string;
  address: string;
  acceptedCategories: FoodCategory[];
  document: DocumentMeta;
  verificationDocRef: string;
}

export interface RegistrationConfirmation {
  submitted: true;
  uid: string;
}

// --- AI classification ------------------------------------------------------

export type ClassificationResult =
  | { ok: true; suggestedCategory: FoodCategory; confidenceScore: number }
  | { ok: false };

// --- Auth -------------------------------------------------------------------

export function onAuthChange(
  cb: (user: FirebaseUser | null) => void,
): () => void {
  return onAuthStateChanged(auth, cb);
}

export async function signInWithGoogle(): Promise<void> {
  // Google Sign-In is the only authentication method (Req 1.1, 1.2).
  googleProvider.setCustomParameters({ prompt: "select_account" });
  await signInWithPopup(auth, googleProvider);
}

export async function signOutUser(): Promise<void> {
  await signOut(auth);
}

export { GoogleAuthProvider };

// --- Callable helper --------------------------------------------------------

function call<TReq, TRes>(name: string) {
  const fn = httpsCallable<TReq, TRes>(functions, name);
  return async (data: TReq): Promise<TRes> => {
    const result = await fn(data);
    return result.data;
  };
}

// --- Session ----------------------------------------------------------------

export async function resolveSession(): Promise<ResolvedSession> {
  return call<Record<string, never>, ResolvedSession>("resolveSession")(
    {} as Record<string, never>,
  );
}

// --- Registration -----------------------------------------------------------

export const registerRestaurant = call<
  RegisterRestaurantInput,
  RegistrationConfirmation
>("registerRestaurant");

export const registerNGO = call<RegisterNGOInput, RegistrationConfirmation>(
  "registerNGO",
);

export const createAdminAccount = call<
  { uid: string; email: string },
  { uid: string; role: "Admin" }
>("createAdminAccount");

// --- Verification -----------------------------------------------------------

export const approveUser = call<{ targetUid: string }, unknown>("approveUser");
export const rejectUser = call<{ targetUid: string }, unknown>("rejectUser");
export const suspendUser = call<{ targetUid: string }, unknown>("suspendUser");
export const removeUser = call<{ targetUid: string }, unknown>("removeUser");

// --- Donations --------------------------------------------------------------

export interface DonationDraftInput {
  foodName: string;
  description: string;
  imageRefs: string[];
  mealCount: number;
  foodCategory: FoodCategory;
  preparationTime: number;
  pickupDeadline: number;
  specialInstructions: string;
}

export const saveDraftDonation = call<DonationDraftInput, { donationId: string }>(
  "saveDraftDonation",
);
export const publishDonation = call<{ donationId: string }, unknown>(
  "publishDonation",
);
export const editDonation = call<
  { donationId: string; changes: Partial<DonationDraftInput> },
  unknown
>("editDonation");
export const cancelDonation = call<{ donationId: string }, unknown>(
  "cancelDonation",
);
export const deleteDonation = call<{ donationId: string }, unknown>(
  "deleteDonation",
);

// --- Reservations -----------------------------------------------------------

export const reserveDonation = call<{ donationId: string }, unknown>(
  "reserveDonation",
);
export const cancelReservation = call<{ donationId: string }, unknown>(
  "cancelReservation",
);
export const markCollected = call<{ donationId: string }, unknown>(
  "markCollected",
);

// --- AI ---------------------------------------------------------------------

export async function classifyFood(
  foodName: string,
  imageRefs: string[],
): Promise<ClassificationResult> {
  try {
    const data = await call<
      { foodName: string; imageRefs: string[] },
      { suggestedCategory: FoodCategory; confidenceScore: number }
    >("classifyFood")({ foodName, imageRefs });
    return { ok: true, ...data };
  } catch {
    // AI failure never blocks the flow; the caller falls back to manual
    // category selection (Req 9.4, 10.1-10.4).
    return { ok: false };
  }
}

// --- Ratings / Reports ------------------------------------------------------

export const submitRating = call<
  { donationId: string; stars: number; reviewText: string },
  unknown
>("submitRating");

export const fileReport = call<
  { targetUid: string; reason: ReportReason },
  unknown
>("fileReport");

export const resolveReport = call<
  { reportId: string; action: "suspend" | "dismiss" | "remove" },
  unknown
>("resolveReport");

// --- Analytics --------------------------------------------------------------

export interface RestaurantOverviewCounts {
  mealsDonated: number;
  mealsCompleted: number;
  activeDonations: number;
  pendingDonations: number;
}
export interface NGOOverviewCounts {
  mealsReceived: number;
  activeClaims: number;
  completedClaims: number;
}
export interface AdminAnalyticsCounts {
  restaurants: number;
  ngos: number;
  mealsDonated: number;
  mealsReceived: number;
  completedDonations: number;
  expiredDonations: number;
}

export const restaurantOverview = (): Promise<RestaurantOverviewCounts> =>
  call<undefined, RestaurantOverviewCounts>("restaurantOverview")(undefined);
export const ngoOverview = (): Promise<NGOOverviewCounts> =>
  call<undefined, NGOOverviewCounts>("ngoOverview")(undefined);
export const adminAnalytics = (): Promise<AdminAnalyticsCounts> =>
  call<undefined, AdminAnalyticsCounts>("adminAnalytics")(undefined);

export const impactStats = (): Promise<unknown> =>
  call<undefined, unknown>("impactStats")(undefined);

// --- Phone visibility -------------------------------------------------------

export async function getPhoneNumber(
  targetUid: string,
): Promise<string | null> {
  const data = await call<{ targetUid: string }, { phoneNumber: string | null }>(
    "getPhoneNumber",
  )({ targetUid });
  return data.phoneNumber;
}

// --- Storage ----------------------------------------------------------------

/** Upload a donation image and return its storage path. */
export async function uploadDonationImage(
  uid: string,
  file: File,
): Promise<string> {
  const path = `donations/${uid}/${Date.now()}-${file.name}`;
  await uploadBytes(ref(storage, path), file);
  return path;
}

/** Upload a verification document and return its admin-only storage path. */
export async function uploadVerificationDocument(
  uid: string,
  file: File,
): Promise<string> {
  const path = `verification-docs/${uid}/${Date.now()}-${file.name}`;
  await uploadBytes(ref(storage, path), file);
  return path;
}

// --- Guarded Firestore reads ------------------------------------------------

function mapDocs<T>(snapshot: { docs: { data: () => unknown }[] }): T[] {
  return snapshot.docs.map((d) => d.data() as T);
}

export async function listDonationsByRestaurant(
  restaurantUid: string,
): Promise<Donation[]> {
  const snap = await getDocs(
    query(collection(db, "donations"), where("restaurantUid", "==", restaurantUid)),
  );
  return mapDocs<Donation>(snap);
}

export async function listAvailableDonations(): Promise<Donation[]> {
  const snap = await getDocs(
    query(collection(db, "donations"), where("status", "==", "Available")),
  );
  return mapDocs<Donation>(snap);
}

export async function listDonationsByReserver(
  ngoUid: string,
): Promise<Donation[]> {
  const snap = await getDocs(
    query(collection(db, "donations"), where("reservedBy", "==", ngoUid)),
  );
  return mapDocs<Donation>(snap);
}

export async function listAllDonations(): Promise<Donation[]> {
  const snap = await getDocs(collection(db, "donations"));
  return mapDocs<Donation>(snap);
}

export async function listNGOProfiles(): Promise<NGOProfile[]> {
  const snap = await getDocs(collection(db, "ngoProfiles"));
  return mapDocs<NGOProfile>(snap);
}

export async function getRestaurantProfile(
  uid: string,
): Promise<RestaurantProfile | null> {
  const snap = await getDoc(doc(db, "restaurantProfiles", uid));
  return snap.exists() ? (snap.data() as RestaurantProfile) : null;
}

export async function getNGOProfile(uid: string): Promise<NGOProfile | null> {
  const snap = await getDoc(doc(db, "ngoProfiles", uid));
  return snap.exists() ? (snap.data() as NGOProfile) : null;
}

export async function listRatingsForNGO(ngoUid: string): Promise<Rating[]> {
  const snap = await getDocs(
    query(collection(db, "ratings"), where("ngoUid", "==", ngoUid)),
  );
  return mapDocs<Rating>(snap);
}

export async function listNotifications(
  recipientUid: string,
): Promise<Notification[]> {
  const snap = await getDocs(
    query(
      collection(db, "notifications"),
      where("recipientUid", "==", recipientUid),
    ),
  );
  return mapDocs<Notification>(snap);
}

export async function listAchievements(
  userUid: string,
): Promise<Achievement[]> {
  const snap = await getDocs(
    query(collection(db, "achievements"), where("userUid", "==", userUid)),
  );
  return mapDocs<Achievement>(snap);
}

export async function listPendingUsers(): Promise<
  { uid: string; role: Role; email: string }[]
> {
  const snap = await getDocs(
    query(
      collection(db, "users"),
      where("verificationStatus", "==", "PendingVerification"),
    ),
  );
  return mapDocs<{ uid: string; role: Role; email: string }>(snap);
}

export async function listPendingReports(): Promise<Report[]> {
  const snap = await getDocs(
    query(collection(db, "reports"), where("status", "==", "Pending")),
  );
  return mapDocs<Report>(snap);
}
